/**
 * **움직이지 않는 물체의 행렬을 얼린다** (2026-09-27).
 *
 * three 는 매 프레임 씬 전체를 돌며 물체마다 위치·회전·크기로 행렬을 다시 만든다(matrixAutoUpdate). 이 게임은 물체가
 * 1만 1천 개인데 벽·가구 대부분은 한 번 놓이면 안 움직인다 — 조합 홀에서 이 일만 프레임당 2.8ms(프레임 JS 의 27%)였고,
 * 큰 방에서 맥북 CPU 가 8.5W 를 먹던 몫의 하나였다(Wayne: 「조사하러 다니다 보니 발열」).
 *
 * - 얼리지 않는 것: 조명과 조명 타깃(조명 풀이 옮긴다), 뼈·스킨 메시, 커튼.
 * - `animated`(서랍·문짝·경첩·도는 물건)도 얼린다 — **움직인 프레임에 props.ts animateProps 가 제 가지만 직접 갱신한다**.
 *   늘 계산하게 두면 three 가 그 아래 가지 전체의 월드 행렬을 매 프레임 다시 곱했고(2.8 → 2.36ms 에 그쳤다), 조상의 순회를
 *   못 끊어 소품 가지 전체를 매 프레임 방문했다.
 * - 나머지는 전부 얼린다(행렬을 한 번 만들고 다시 안 만든다). 위의 것 아래에 있는 것도 제 행렬은 얼린다 — 부모가 움직이면
 *   three 가 월드 행렬만 다시 곱한다. 위아래로 움직이는 것이 하나도 없는 가지는 아예 안 돈다.
 * - **틀리면 스스로 푼다**: 언 물체의 위치·회전·크기를 8프레임에 한 번씩(나눠서) 보고, 바뀌었으면(떨어뜨린 소품, 미처 몰랐던
 *   애니메이션) 그 물체를 푼다. 늦어야 8프레임.
 * - 언 가지에 나중에 무언가 붙으면(add·attach) 그 가지는 다시 돈다. 인게임 편집기(`)는 조각을 옮기므로 켤 때 전부 푼다.
 */
import * as THREE from 'three';

export interface Frozen {
  /** 매 프레임 — 언 물체 일부가 움직였는지 본다 */
  update(): void;
  /** 전부 푼다(편집기) */
  thaw(): void;
  stats(): { frozen: number; skipped: number; thawed: number };
}

export function freezeStatic(root: THREE.Object3D, animated: (o: THREE.Object3D) => boolean): Frozen {
  root.updateMatrixWorld(true);
  const targets = new Set<THREE.Object3D>();
  root.traverse((o) => { const t = (o as THREE.SpotLight).target; if ((o as THREE.Light).isLight && t) targets.add(t); });
  /** 늘 제 행렬을 만든다 — 조명 풀이 옮기는 조명·타깃, 뼈 */
  const keep = (o: THREE.Object3D) => {
    const a = o as THREE.Object3D & { isLight?: boolean; isCamera?: boolean; isBone?: boolean; isSkinnedMesh?: boolean };
    return o.name === 'curtain' || !!a.isLight || !!a.isCamera || !!a.isBone || !!a.isSkinnedMesh || targets.has(o);
  };
  const frozen: THREE.Object3D[] = [];
  /** 언 물체마다 얼릴 때의 위치·회전·크기(10개) */
  let snap = new Float64Array(0);
  let skipped = 0, thawed = 0;
  /** @returns 이 노드부터 아래가 전부 얼었는가 */
  /**
   * 움직이는 부품 — 제 행렬도 얼리고 가지째 건너뛴다. 움직인 프레임에 props.ts animateProps 가 **제 가지만** 직접 갱신한다
   * (updateMatrix + updateMatrixWorld(true)). 조상이 순회를 끊어도 그래서 따라간다
   */
  const anim: THREE.Object3D[] = [];
  const walk = (o: THREE.Object3D, keptAbove: boolean): boolean => {
    const k = keep(o);
    const moves = k;
    let all = !moves;
    for (const c of o.children) if (!walk(c, keptAbove || moves)) all = false;
    if (!k) {
      o.matrixAutoUpdate = false;
      if (animated(o)) anim.push(o);
      else {
        frozen.push(o);
        // 위에도 아래에도 움직이는 것이 없으면 가지째 안 돈다(위가 움직이면 월드 행렬을 다시 곱해야 하니 안 된다)
        if (all && !keptAbove) { o.matrixWorldAutoUpdate = false; skipped++; }
      }
    }
    return all;
  };
  for (const c of root.children) walk(c, false);
  // 루트도 얼린다 — 루트가 제 행렬을 매 프레임 다시 만들면 three 는 「아래 전부 다시 계산」을 강제한다(force). 1만 개가 매 프레임
  // 월드 행렬을 곱하던 원인이 이것(과 씬 자체 — main.ts)이었다
  root.matrixAutoUpdate = false;
  /*
   * **가지째 건너뛰기.** three r185 의 updateMatrixWorld 는 matrixWorldAutoUpdate 와 상관없이 **모든 자식을 방문한다** —
   * 할 일이 없어도 1만 번 함수를 부른다. 통째로 언 가지의 머리는 방문을 끊는다. 위에서 다시 계산을 강제하면(force — 조상이
   * 움직임) 그 가지는 풀어서 원래대로 계산한다
   */
  const base = THREE.Object3D.prototype.updateMatrixWorld;
  root.traverse((o) => {
    if (o.matrixWorldAutoUpdate !== false || o === root) return;
    o.updateMatrixWorld = function (this: THREE.Object3D, force?: boolean) {
      if (force && !this.matrixWorldAutoUpdate) this.matrixWorldAutoUpdate = true;
      if (this.matrixWorldAutoUpdate) base.call(this, force);
    };
  });
  snap = new Float64Array(frozen.length * 10);
  const write = (i: number) => {
    const o = frozen[i], b = i * 10;
    snap[b] = o.position.x; snap[b + 1] = o.position.y; snap[b + 2] = o.position.z;
    snap[b + 3] = o.quaternion.x; snap[b + 4] = o.quaternion.y; snap[b + 5] = o.quaternion.z; snap[b + 6] = o.quaternion.w;
    snap[b + 7] = o.scale.x; snap[b + 8] = o.scale.y; snap[b + 9] = o.scale.z;
  };
  for (let i = 0; i < frozen.length; i++) write(i);

  /** 이 노드와 위쪽을 다시 돌게 한다 */
  const reopen = (o: THREE.Object3D | null) => {
    for (let q = o; q && q !== root; q = q.parent) { q.matrixWorldAutoUpdate = true; q.matrixWorldNeedsUpdate = true; }
  };
  const done = new Uint8Array(frozen.length);
  const thawAt = (i: number) => {
    const o = frozen[i];
    done[i] = 1;
    thawed++;
    o.matrixAutoUpdate = true;
    reopen(o);
  };
  // 언 가지에 무언가 붙으면 그 가지(와 위쪽)는 다시 돈다 — 안 그러면 붙은 것의 월드 행렬이 영영 안 만들어진다
  for (const o of frozen) {
    const add = o.add.bind(o), attach = o.attach.bind(o);
    o.add = (...objs: THREE.Object3D[]) => { add(...objs); reopen(o); return o; };
    o.attach = (obj: THREE.Object3D) => { attach(obj); reopen(o); return o; };
  }

  let frame = 0;
  const update = () => {
    frame++;
    const slice = frame & 7;
    for (let i = slice; i < frozen.length; i += 8) {
      if (done[i]) continue;
      const o = frozen[i], b = i * 10;
      if (o.position.x !== snap[b] || o.position.y !== snap[b + 1] || o.position.z !== snap[b + 2]
        || o.quaternion.x !== snap[b + 3] || o.quaternion.y !== snap[b + 4] || o.quaternion.z !== snap[b + 5] || o.quaternion.w !== snap[b + 6]
        || o.scale.x !== snap[b + 7] || o.scale.y !== snap[b + 8] || o.scale.z !== snap[b + 9]) thawAt(i);
    }
  };

  return {
    update,
    thaw: () => { for (let i = 0; i < frozen.length; i++) if (!done[i]) thawAt(i); for (const o of anim) o.matrixAutoUpdate = true; },
    stats: () => ({ frozen: frozen.length - thawed, skipped, thawed }),
  };
}
